import { useState } from "react";
import SiteNav from "@/components/feature/SiteNav";
import SiteFooter from "@/components/feature/SiteFooter";
import PageHeader from "@/components/feature/PageHeader";

const FORM_URL = "https://readdy.ai/api/form/dap39i0d8872q3cdc270";

const PROJECT_TYPES = ["Whole Home", "Kitchen", "Bathroom", "Addition", "Other"];
const BUDGETS = ["$150k – $350k", "$350k – $750k", "$750k – $1.5M", "$1.5M+"];
const TIMELINES = ["0–3 months", "3–6 months", "6–12 months", "12+ months"];

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactPage() {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // Message length cap
    const message = String(fd.get("message") ?? "");
    if (message.length > 500) {
      setStatus("error");
      setErrorMsg("Please keep your message under 500 characters.");
      return;
    }

    // Honeypot
    const hp = String(fd.get("website_alt") ?? "").trim();
    if (hp) {
      setStatus("success");
      form.reset();
      return;
    }
    fd.delete("website_alt");

    setStatus("submitting");
    setErrorMsg("");

    const params = new URLSearchParams();
    fd.forEach((v, k) => params.append(k, String(v)));

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const text = await res.text();
      let parsed: any = null;
      try { parsed = JSON.parse(text); } catch { /* noop */ }
      const code = parsed?.code;
      const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text || "";

      if (res.ok && code === "OK" && !/spam/i.test(String(msg))) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setErrorMsg(String(msg) || "Something went wrong. Please try again.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg("Network error. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-warm-bg text-ink">
      <SiteNav />
      <PageHeader
        eyebrow="Start a Project"
        title={<>Tell us about your home.</>}
        intro="Share a few details about the project you have in mind. We reply within two business days to arrange an introductory conversation."
        crumbs={[{ label: "Home", to: "/" }, { label: "Contact" }]}
      />

      <section className="w-full pb-24 md:pb-28">
        <div className="w-full grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16" style={{ paddingLeft: "clamp(20px, 5vw, 96px)", paddingRight: "clamp(20px, 5vw, 96px)" }}>
          <div className="md:col-span-8">
            {status === "success" ? (
              <div className="border-t hairline pt-10">
                <span className="eyebrow">Received</span>
                <h2 className="mt-5 font-serif-display font-light leading-[1.05]"
                    style={{ fontSize: "clamp(32px,2.6vw,42px)", letterSpacing: "-0.015em" }}>
                  Thank you — we&rsquo;ll be in touch.
                </h2>
                <p className="mt-5 text-ink-soft leading-[1.65]" style={{ fontSize: "16px" }}>
                  Your inquiry is with the studio. Expect a reply within two business days.
                </p>
              </div>
            ) : (
              <form id="project-inquiry-form" data-readdy-form onSubmit={onSubmit} noValidate
                    className="flex flex-col gap-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Field name="name" label="Name" required />
                  <Field name="email" label="Email" type="email" required />
                  <Field name="phone" label="Phone" type="tel" />
                  <Field name="location" label="Project Location" placeholder="City, State" />
                </div>

                <SelectField name="project_type" label="Project Type" options={PROJECT_TYPES} required />
                <SelectField name="budget" label="Estimated Investment" options={BUDGETS} />
                <SelectField name="timeline" label="Desired Timeline" options={TIMELINES} />

                <div className="flex flex-col gap-2">
                  <label htmlFor="message" className="eyebrow">Tell us about your home</label>
                  <textarea id="message" name="message" rows={5} maxLength={500}
                            className="w-full bg-transparent border-b hairline focus:border-accent outline-none py-3 text-[16px] leading-[1.6] resize-y" />
                </div>

                {/* Honeypot */}
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off"
                       aria-hidden="true" readOnly className="hp-field" />

                {status === "error" && (
                  <div className="text-[14px] text-accent border-t hairline pt-4">{errorMsg}</div>
                )}

                <div className="pt-4 border-t hairline flex items-center justify-between gap-6">
                  <span className="text-[13px] text-ink-soft">We reply within two business days.</span>
                  <button type="submit" disabled={status === "submitting"}
                          className="arrow-link text-[12px] tracking-[0.14em] uppercase text-ink hover:text-accent transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60">
                    {status === "submitting" ? "Sending…" : "Submit Project Inquiry"}
                    <i className="ri-arrow-right-line arrow w-4 h-4 flex items-center justify-center" />
                  </button>
                </div>
              </form>
            )}
          </div>

          <aside className="md:col-span-4 md:pl-10 md:border-l hairline flex flex-col gap-10">
            <div>
              <span className="eyebrow">Studio</span>
              <p className="mt-4 text-[15px] leading-[1.7]">
                Atelier Ridge<br />
                1420 Fair Oaks Ave<br />
                Pasadena, California
              </p>
            </div>
            <div>
              <span className="eyebrow">Contact</span>
              <p className="mt-4 text-[15px] leading-[1.7]">
                <a href="mailto:studio@atelierridge.com" className="hover:text-accent cursor-pointer">studio@atelierridge.com</a><br />
                <a href="tel:+16265550142" className="hover:text-accent cursor-pointer">+1 (626) 555-0142</a>
              </p>
            </div>
            <div>
              <span className="eyebrow">Hours</span>
              <p className="mt-4 text-[15px] leading-[1.7]">
                Monday – Friday<br />
                9:00 – 18:00 PT<br />
                By appointment
              </p>
            </div>
          </aside>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function Field({ name, label, type = "text", required, placeholder }:
  { name: string; label: string; type?: string; required?: boolean; placeholder?: string }) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={name} className="eyebrow">{label}{required && <span className="text-accent"> *</span>}</label>
      <input id={name} name={name} type={type} required={required} placeholder={placeholder}
             className="w-full bg-transparent border-b hairline focus:border-accent outline-none py-3 text-[16px]" />
    </div>
  );
}

function SelectField({ name, label, options, required }:
  { name: string; label: string; options: string[]; required?: boolean }) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={name} className="eyebrow">{label}{required && <span className="text-accent"> *</span>}</label>
      <div className="relative">
        <select id={name} name={name} required={required} defaultValue=""
                className="w-full bg-transparent border-b hairline focus:border-accent outline-none py-3 text-[16px] appearance-none cursor-pointer pr-8">
          <option value="" disabled>Select…</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <i className="ri-arrow-down-s-line absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none w-5 h-5 flex items-center justify-center text-ink-soft" />
      </div>
    </div>
  );
}